import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Card,
  CardHeader,
  CardBody,
  Input,
  Spinner,
  Nav,
  NavItem,
  NavLink,
  Dropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
  Modal,
  Row,
  Col,
} from 'reactstrap';
import DatePicker from 'react-datepicker';
import Select from 'react-select';
import FeatherIcon from 'feather-icons-react';
import classnames from 'classnames';
import PermissionGuard from '../../components/Common/PermissionGuard';
import usePermission from '../../Hooks/usePermission';
import {
  MATERIOVIGILANCE_CREATE,
  MATERIOVIGILANCE_DELETE,
} from '../../constants/permissions';
import { fetchMaterioList, deleteDesc, deleteAsc } from '../../store/materiovigilance/actions';
import { formatCreatedDate } from '../../constants/utils';
import api from '../../helpers/api';
import MaterioStatusBadge from './MaterioStatusBadge';
import MaterioFlowBadge from './MaterioFlowBadge';
import MaterioDescendingModal from './MaterioDescendingModal';
import MaterioAscendingModal from './MaterioAscendingModal';

const PAGE_SIZE = 15;

const Materiovigilance = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { hasPermission } = usePermission();

  const { list, listLoading, listError, listPagination } = useSelector((s) => s.Materiovigilance);

  const authUser = JSON.parse(localStorage.getItem('authUser') || '{}');
  const orgId = authUser?.activeOrganizationId ?? null;

  const [searchTerm, setSearchTerm] = useState('');
  const [flowFilter, setFlowFilter] = useState('ALL');
  const [statusFilter, setStatusFilter] = useState('');
  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);
  const [dateRangeModal, setDateRangeModal] = useState(false);
  const [filterModal, setFilterModal] = useState(false);
  const [assetFilter, setAssetFilter] = useState(null);
  const [recipientFilter, setRecipientFilter] = useState(null);
  const [assetOptions, setAssetOptions] = useState([]);
  const [recipientOptions, setRecipientOptions] = useState([]);
  const [page, setPage] = useState(0);
  const [createOpen, setCreateOpen] = useState(false);

  useEffect(() => {
    if (!filterModal || !orgId) return;
    let cancelled = false;
    (async () => {
      try {
        const [assetsRes, usersRes] = await Promise.all([
          api.get('asset/all', { params: { count: 100, hierarchy: false, organizationId: orgId } }),
          api.get('user/with-permission', { params: { organizationId: orgId, permission: 'materiovigilance:read' } }),
        ]);
        if (cancelled) return;
        const rawAssets = assetsRes?.data?.response;
        const assets = Array.isArray(rawAssets) ? (rawAssets[0]?.assets || rawAssets) : (rawAssets?.assets || rawAssets?.content || []);
        setAssetOptions((assets || []).map((a) => ({
          value: a.id,
          label: `${a.assetName || a.name || `#${a.id}`}${a.serialNumber ? ` · ${a.serialNumber}` : ''}`,
        })));
        const users = usersRes?.data?.response || [];
        setRecipientOptions(users.map((u) => ({ value: u.id, label: u.name || u.fullName || u.email || `#${u.id}` })));
      } catch (e) {
        if (!cancelled) { setAssetOptions([]); setRecipientOptions([]); }
      }
    })();
    return () => { cancelled = true; };
  }, [filterModal, orgId]);

  // Modal state
  const [descModal, setDescModal] = useState({ isOpen: false, mode: 'create', recordId: null });
  const [ascModal, setAscModal] = useState({ isOpen: false, mode: 'create', recordId: null });

  const toIsoDate = (d) => {
    if (!d) return null;
    const tzOffset = d.getTimezoneOffset() * 60000;
    return new Date(d.getTime() - tzOffset).toISOString().slice(0, 10);
  };

  const refresh = () => {
    const queryParams = { page, size: PAGE_SIZE };
    if (flowFilter !== 'ALL') queryParams.flowKind = flowFilter;
    if (statusFilter) queryParams.status = statusFilter;
    if (searchTerm.trim()) queryParams.q = searchTerm.trim();
    if (fromDate) queryParams.dateFrom = toIsoDate(fromDate);
    if (toDate) queryParams.dateTo = toIsoDate(toDate);
    if (assetFilter?.value) queryParams.assetId = assetFilter.value;
    if (recipientFilter?.value) queryParams.recipientUserId = recipientFilter.value;
    dispatch(fetchMaterioList(queryParams));
  };

  useEffect(() => { refresh(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [page, flowFilter, statusFilter, fromDate, toDate, assetFilter, recipientFilter]);

  const activeFilterCount = (statusFilter ? 1 : 0) + (assetFilter ? 1 : 0) + (recipientFilter ? 1 : 0);

  useEffect(() => {
    const timer = setTimeout(() => { setPage(0); refresh(); }, 300);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm]);

  const handleSearch = (e) => setSearchTerm(e.target.value);

  const openCreateDescending = () => setDescModal({ isOpen: true, mode: 'create', recordId: null });
  const openCreateAscending = () => setAscModal({ isOpen: true, mode: 'create', recordId: null });
  const openRow = (row) => {
    if (row.flowKind === 'DESCENDING') {
      setDescModal({ isOpen: true, mode: 'view', recordId: row.id });
    } else {
      setAscModal({ isOpen: true, mode: 'view', recordId: row.id });
    }
  };
  const closeDesc = () => setDescModal((m) => ({ ...m, isOpen: false }));
  const closeAsc = () => setAscModal((m) => ({ ...m, isOpen: false }));

  const handleDelete = (e, row) => {
    e.stopPropagation();
    const ok = window.confirm(t('compliance.materiovigilance.confirm.delete', 'Delete this materiovigilance record?'));
    if (!ok) return;
    if (row.flowKind === 'DESCENDING') {
      dispatch(deleteDesc(row.id, refresh));
    } else {
      dispatch(deleteAsc(row.id, refresh));
    }
  };

  const totalPages = listPagination?.totalPages || 1;
  const showingCount = list?.length || 0;
  const totalElements = listPagination?.totalElements ?? showingCount;

  const FLOW_TABS = [
    { key: 'ALL', label: t('compliance.materiovigilance.flow.all', 'All'), icon: 'list' },
    { key: 'DESCENDING', label: t('compliance.materiovigilance.flow.descending_full', 'Descending Materiovigilance'), icon: 'arrow-down' },
    { key: 'ASCENDING', label: t('compliance.materiovigilance.flow.ascending_full', 'Ascending Materiovigilance'), icon: 'arrow-up' },
  ];

  return (
    <div className="page-content mt-4">
      <motion.div
        className="mb-4 d-flex align-items-center gap-3 w-100"
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
      >
        <PermissionGuard permission={MATERIOVIGILANCE_CREATE}>
          <Dropdown isOpen={createOpen} toggle={() => setCreateOpen((v) => !v)}>
            <DropdownToggle color="success" caret>
              <FeatherIcon icon="plus" size={18} className="me-1" />
              {t('compliance.materiovigilance.add', 'Add Materiovigilance')}
            </DropdownToggle>
            <DropdownMenu>
              <DropdownItem onClick={openCreateDescending}>
                <span style={{ marginRight: 6 }}>↓</span>
                {t('compliance.materiovigilance.new_descending_full', 'Descending Materiovigilance')}
              </DropdownItem>
              <DropdownItem onClick={openCreateAscending}>
                <span style={{ marginRight: 6 }}>↑</span>
                {t('compliance.materiovigilance.new_ascending_full', 'Ascending Materiovigilance')}
              </DropdownItem>
            </DropdownMenu>
          </Dropdown>
        </PermissionGuard>

        <div style={{ minWidth: '400px', flexGrow: 1, maxWidth: '500px' }}>
          <Input
            type="text"
            placeholder={t('compliance.materiovigilance.search_placeholder', 'Search alert #, description…')}
            value={searchTerm}
            onChange={handleSearch}
          />
        </div>
      </motion.div>

      <Card>
        <Nav pills className="justify-content-start px-3 pt-3 gap-2">
          {FLOW_TABS.map((tab) => (
            <NavItem key={tab.key}>
              <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                <NavLink
                  style={{ cursor: 'pointer' }}
                  className={classnames('tab-btn', { active: flowFilter === tab.key })}
                  onClick={() => { setFlowFilter(tab.key); setPage(0); }}
                >
                  <FeatherIcon icon={tab.icon} size={14} className="me-1" />
                  {tab.label}
                </NavLink>
              </motion.div>
            </NavItem>
          ))}
        </Nav>

        <CardHeader>
          <div className="d-flex justify-content-between align-items-center">
            <h3 className="card-title text-success mb-0">
              {t('compliance.materiovigilance.title', 'Materiovigilance')}
            </h3>
            <div className="d-flex align-items-center gap-2">
              <button
                type="button"
                className={`btn btn-sm p-2 ${fromDate ? 'btn-soft-primary' : 'btn-soft-secondary'}`}
                onClick={() => setDateRangeModal(true)}
                title={t('shared.date_range_filter', 'Date range filter')}
              >
                <FeatherIcon icon="calendar" size={16} />
                {fromDate && (
                  <span className="ms-1 small">
                    {formatCreatedDate(fromDate)} - {toDate ? formatCreatedDate(toDate) : '...'}
                  </span>
                )}
              </button>
              <button
                type="button"
                className={`btn btn-sm p-2 ${activeFilterCount > 0 ? 'btn-soft-primary' : 'btn-soft-secondary'}`}
                onClick={() => setFilterModal(true)}
                title={t('shared.filter', 'Filter')}
              >
                <FeatherIcon icon="filter" size={16} />
                {activeFilterCount > 0 && (
                  <span className="ms-1 small">{activeFilterCount}</span>
                )}
              </button>
            </div>
          </div>
        </CardHeader>

        <CardBody>
          {listError && (
            <div className="alert alert-danger">
              {t('compliance.materiovigilance.error', 'Could not load records.')} {String(listError)}
            </div>
          )}

          <div
            className="table-responsive"
            style={{
              overflowX: 'auto',
              whiteSpace: 'nowrap',
              maxHeight: 'calc(100vh - 360px)',
              overflowY: 'auto',
            }}
          >
            <table
              className="table table-hover mb-0"
              style={{ borderSpacing: '0.1rem', borderCollapse: 'separate' }}
            >
              <thead style={{ position: 'sticky', top: 0, background: 'white', zIndex: 1 }}>
                <tr>
                  <th>#</th>
                  <th>{t('compliance.materiovigilance.field.alert_number', 'Alert #')}</th>
                  <th>{t('compliance.materiovigilance.field.flow', 'Flow')}</th>
                  <th>{t('compliance.materiovigilance.field.incident_date', 'Incident date')}</th>
                  <th>{t('compliance.materiovigilance.field.status', 'Status')}</th>
                  <th>{t('compliance.materiovigilance.field.issuer_user', 'Created by')}</th>
                  <th>{t('compliance.materiovigilance.field.summary', 'Summary')}</th>
                  <th>{t('compliance.materiovigilance.field.linked_assets', 'Assets')}</th>
                  {hasPermission(MATERIOVIGILANCE_DELETE) && <th>{t('shared.delete', 'Delete')}</th>}
                </tr>
              </thead>
              <tbody>
                {listLoading && (
                  <tr>
                    <td colSpan={hasPermission(MATERIOVIGILANCE_DELETE) ? 9 : 8} className="text-center py-4">
                      <Spinner size="sm" />
                    </td>
                  </tr>
                )}
                {!listLoading && (!list || list.length === 0) && (
                  <tr>
                    <td colSpan={hasPermission(MATERIOVIGILANCE_DELETE) ? 9 : 8} className="text-center text-muted py-4">
                      {searchTerm
                        ? t('compliance.materiovigilance.no_results', 'No records match your search.')
                        : t('compliance.materiovigilance.empty', 'No materiovigilance records yet.')}
                    </td>
                  </tr>
                )}
                <AnimatePresence>
                  {!listLoading && list && list.map((row, idx) => (
                    <motion.tr
                      key={`${row.flowKind}-${row.id}`}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -20 }}
                      transition={{ duration: 0.3 }}
                      style={{ cursor: 'pointer' }}
                      onClick={() => openRow(row)}
                    >
                      <th scope="row">{idx + 1 + page * PAGE_SIZE}</th>
                      <td><span className="text-primary">{row.alertNumber}</span></td>
                      <td><MaterioFlowBadge flowKind={row.flowKind} /></td>
                      <td className="text-muted">{row.incidentDate ? formatCreatedDate(row.incidentDate) : '—'}</td>
                      <td><MaterioStatusBadge status={row.status} /></td>
                      <td className="text-muted">{row.issuerUserName || '—'}</td>
                      <td className="text-muted small" title={row.summary || ''}>
                        <div
                          style={{
                            maxWidth: 320,
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                          }}
                        >
                          {row.summary || '—'}
                        </div>
                      </td>
                      <td className="text-center text-muted">{row.linkedAssetCount ?? 0}</td>
                      {hasPermission(MATERIOVIGILANCE_DELETE) && (
                        <td>
                          <span
                            className="text-danger"
                            role="button"
                            title={t('shared.delete', 'Delete')}
                            onClick={(e) => handleDelete(e, row)}
                          >
                            <FeatherIcon icon="trash-2" />
                          </span>
                        </td>
                      )}
                    </motion.tr>
                  ))}
                </AnimatePresence>
              </tbody>
            </table>
          </div>

          {totalPages > 1 && (
            <div className="d-flex align-items-center justify-content-between mt-3">
              <small className="text-muted">
                {t('shared.showing', 'Showing')} {showingCount} / {totalElements}
              </small>
              <div className="d-flex" style={{ gap: 4 }}>
                <button
                  type="button"
                  className="btn btn-sm btn-outline-secondary"
                  disabled={page === 0}
                  onClick={() => setPage((p) => Math.max(0, p - 1))}
                >
                  <FeatherIcon icon="chevron-left" size={14} />
                </button>
                <span className="px-2 align-self-center small">{page + 1} / {totalPages}</span>
                <button
                  type="button"
                  className="btn btn-sm btn-outline-secondary"
                  disabled={page >= totalPages - 1}
                  onClick={() => setPage((p) => p + 1)}
                >
                  <FeatherIcon icon="chevron-right" size={14} />
                </button>
              </div>
            </div>
          )}
        </CardBody>
      </Card>

      <Modal
        isOpen={dateRangeModal}
        toggle={() => setDateRangeModal(false)}
        centered
      >
        <div className="modal-header">
          <h5 className="modal-title">{t('shared.select_date_range', 'Select Date Range')}</h5>
          <button
            type="button"
            className="btn-close"
            onClick={() => setDateRangeModal(false)}
            aria-label={t('shared.close', 'Close')}
          ></button>
        </div>
        <div className="modal-body p-4">
          <Row className="g-3">
            <Col md={6}>
              <label htmlFor="materioFromDate" className="form-label fw-medium">
                {t('shared.from_date', 'From Date')}
              </label>
              <DatePicker
                id="materioFromDate"
                selected={fromDate}
                onChange={(date) => { setFromDate(date); setPage(0); }}
                selectsStart
                startDate={fromDate}
                endDate={toDate}
                className="form-control"
                dateFormat="dd/MM/yyyy"
                placeholderText={t('shared.select_start_date', 'Select start date')}
                isClearable
                showMonthDropdown
                showYearDropdown
                dropdownMode="select"
              />
            </Col>
            <Col md={6}>
              <label htmlFor="materioToDate" className="form-label fw-medium">
                {t('shared.to_date', 'To date')}
              </label>
              <DatePicker
                id="materioToDate"
                selected={toDate}
                onChange={(date) => { setToDate(date); setPage(0); }}
                selectsEnd
                startDate={fromDate}
                endDate={toDate}
                minDate={fromDate}
                className="form-control"
                dateFormat="dd/MM/yyyy"
                placeholderText={t('shared.select_end_date', 'Select end date')}
                isClearable
                disabled={!fromDate}
                showMonthDropdown
                showYearDropdown
                dropdownMode="select"
              />
            </Col>
          </Row>
        </div>
        <div className="modal-footer bg-light border-top-0">
          <button
            type="button"
            className="btn btn-outline-danger"
            onClick={() => {
              setFromDate(null);
              setToDate(null);
              setPage(0);
              setDateRangeModal(false);
            }}
          >
            {t('shared.clear_dates', 'Clear Dates')}
          </button>
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => setDateRangeModal(false)}
            disabled={fromDate && !toDate}
          >
            {t('shared.apply_dates', 'Apply Dates')}
          </button>
        </div>
      </Modal>

      <Modal
        isOpen={filterModal}
        toggle={() => setFilterModal(false)}
        size="lg"
        centered
      >
        <div className="modal-header">
          <h5 className="modal-title">{t('compliance.materiovigilance.filter_title', 'Filter Materiovigilance')}</h5>
          <button
            type="button"
            className="btn-close"
            onClick={() => setFilterModal(false)}
            aria-label={t('shared.close', 'Close')}
          ></button>
        </div>
        <div className="modal-body p-4">
          <Row className="g-3 mb-3">
            <Col md={6}>
              <label htmlFor="filterFlow" className="form-label fw-medium">
                {t('compliance.materiovigilance.field.flow', 'Flow')}
              </label>
              <select
                id="filterFlow"
                className="form-select"
                value={flowFilter}
                onChange={(e) => { setFlowFilter(e.target.value); setPage(0); }}
              >
                <option value="ALL">{t('compliance.materiovigilance.flow.all', 'All')}</option>
                <option value="DESCENDING">{t('compliance.materiovigilance.flow.descending_full', 'Descending Materiovigilance')}</option>
                <option value="ASCENDING">{t('compliance.materiovigilance.flow.ascending_full', 'Ascending Materiovigilance')}</option>
              </select>
            </Col>
            <Col md={6}>
              <label htmlFor="filterStatus" className="form-label fw-medium">
                {t('shared.status', 'Status')}
              </label>
              <select
                id="filterStatus"
                className="form-select"
                value={statusFilter}
                onChange={(e) => { setStatusFilter(e.target.value); setPage(0); }}
              >
                <option value="">{t('compliance.materiovigilance.status.all', 'All statuses')}</option>
                <option value="DRAFT">{t('compliance.materiovigilance.status.draft', 'Draft')}</option>
                <option value="DECLARED">{t('compliance.materiovigilance.status.declared', 'Declared')}</option>
                <option value="UNDER_INVESTIGATION">
                  {t('compliance.materiovigilance.status.under_investigation', 'Under investigation')}
                </option>
                <option value="CLOSED">{t('compliance.materiovigilance.status.closed', 'Closed')}</option>
              </select>
            </Col>
          </Row>
          <Row className="g-3">
            <Col md={6}>
              <label htmlFor="filterAsset" className="form-label fw-medium">
                {t('shared.asset', 'Asset')}
              </label>
              <Select
                inputId="filterAsset"
                isClearable
                isSearchable
                placeholder={t('shared.search_asset', 'Search asset…')}
                options={assetOptions}
                value={assetFilter}
                onChange={(opt) => { setAssetFilter(opt); setPage(0); }}
                menuPortalTarget={document.body}
                styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
              />
            </Col>
            <Col md={6}>
              <label htmlFor="filterRecipient" className="form-label fw-medium">
                {t('compliance.materiovigilance.field.recipient', 'Recipient')}
              </label>
              <Select
                inputId="filterRecipient"
                isClearable
                isSearchable
                placeholder={t('shared.search_user', 'Search user…')}
                options={recipientOptions}
                value={recipientFilter}
                onChange={(opt) => { setRecipientFilter(opt); setPage(0); }}
                menuPortalTarget={document.body}
                styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
              />
            </Col>
          </Row>
        </div>
        <div className="modal-footer bg-light border-top-0">
          <button
            type="button"
            className="btn btn-outline-danger"
            onClick={() => {
              setFlowFilter('ALL');
              setStatusFilter('');
              setAssetFilter(null);
              setRecipientFilter(null);
              setPage(0);
              setFilterModal(false);
            }}
          >
            {t('shared.clear_all', 'Clear All')}
          </button>
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => setFilterModal(false)}
          >
            {t('shared.apply_filters', 'Apply Filters')}
          </button>
        </div>
      </Modal>

      <MaterioDescendingModal
        isOpen={descModal.isOpen}
        toggle={closeDesc}
        mode={descModal.mode}
        recordId={descModal.recordId}
        onChanged={refresh}
      />
      <MaterioAscendingModal
        isOpen={ascModal.isOpen}
        toggle={closeAsc}
        mode={ascModal.mode}
        recordId={ascModal.recordId}
        onChanged={refresh}
      />
    </div>
  );
};

export default Materiovigilance;
